import { computed, effect, EventEmitter, inject, Service, NgZone, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { EEventKiwidSocket } from 'app/core/enums/socket.enum';
import { EventDetectorService } from 'app/core/helpers/event-detector.helper';
import { IResponseSocket } from 'app/core/models/api/data';
import { IAuthentication } from 'app/core/models/authentication/authentication';
import { IErrorWebsocket } from 'app/core/models/error/error';
import { EAccessType } from 'app/core/permissions/enum/access.enum';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, first, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';

@Service()
export class KiwidSocketService {
    private readonly ngZone = inject(NgZone);
    private readonly translateService = inject(TranslateService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly eventDetectorService = inject(EventDetectorService);

    private socket: Socket;
    private reconnectAttempts = 0;
    private readonly maxReconnectAttempts = 5;
    private readonly reconnectInterval = 5000;
    private readonly disableConnectionInterval = 30000;
    private readonly maxRetryReconnectionRefreshToken = 5;
    private retryReconnectionRefreshToken = 0;
    private readonly isConnected$ = new BehaviorSubject<boolean>(false);
    private readonly eventOnline = toSignal(this.eventDetectorService.online());
    private readonly eventOffline = toSignal(this.eventDetectorService.offline());
    private readonly eventVisibilitychange = toSignal(this.eventDetectorService.visibilitychange());
    private readonly isLoadingConnection = signal(true);
    private visibilityTimeout: number | null = null;
    private prevRefreshToken = '';
    private readonly disabledDisconnect = signal(false);

    public readonly disableSocket = signal(this.authenticationService.userIsKiwid);
    public readonly isConnected = toSignal(this.isConnected$.asObservable(), {
        initialValue: this.isConnected$.value,
    });
    public readonly isDisconnect = computed(() => !this.isConnected() && !this.isReconnecting());
    public readonly isReconnecting = signal(false);
    public readonly isConnecting = computed(() => this.isLoadingConnection() && !this.disableSocket());
    public readonly isOffline = signal(!navigator.onLine);
    public readonly onException = new EventEmitter<IErrorWebsocket>();
    public readonly connectionEvent = new EventEmitter<void>();
    public readonly clientId = signal<string | null>(null);

    constructor() {
        effect(() => {
            if (this.disableSocket()) {
                return;
            }

            this.eventOffline();
            this.eventOnline();
            untracked(() => {
                this.isOffline.set(!navigator.onLine);
            });
        });

        let prevIsOffline: boolean | null = null;
        effect((onCleanup) => {
            if (this.disableSocket()) {
                return;
            }

            const isOffline = this.isOffline();

            let timeout: NodeJS.Timeout = null;

            untracked(() => {
                timeout = setTimeout(() => {
                    if (isOffline !== prevIsOffline) {
                        prevIsOffline = isOffline;

                        if (isOffline) {
                            this.disconnect();
                        } else if (!this.isLoadingConnection() && !this.isConnected()) {
                            this.attemptReconnect();
                        }
                    }
                }, 300);
            });

            onCleanup(() => {
                if (!!timeout) {
                    clearTimeout(timeout);
                }
            });
        });

        // effect(() => {
        //     if (this.disableSocket() || this.disabledDisconnect()) {
        //         return;
        //     }

        //     this.eventVisibilitychange();

        //     untracked(() => {
        //         if (document.hidden) {
        //             this.handleTabHidden();
        //         } else {
        //             this.handleTabVisible();
        //         }
        //     });
        // });
    }

    public reconnect(): void {
        this.destroySocket();
        this.initSocket();
    }

    public initSocket() {
        if (this.disableSocket()) {
            return;
        }

        if (this.socket?.active || this.socket?.connected) {
            this.destroySocket();
        }

        this.socket = io(`${environment.apiUrl}`, {
            autoConnect: false,
            reconnectionAttempts: 0,
            reconnection: false,
            transports: ['websocket'],
            auth: {
                token: this.authenticationService.token,
                refreshToken: this.authenticationService.refreshToken,
                accessType: EAccessType.ADMIN_CLINIC,
                clinicId: this.authenticationService.clinicId(),
                lang: this.translateService.currentLang() === 'pt' ? 'pt-br' : this.translateService.currentLang(),
            },
        });

        this.setupSocketListeners();
        this.connect();
    }

    public destroySocket() {
        this.reconnectAttempts = Number(this.maxReconnectAttempts);

        if (!!this.socket) {
            Object.values(EEventKiwidSocket).forEach((key) => {
                this.socket.off(key);
            });

            if (!!this.prevRefreshToken) {
                this.socket.off(EEventKiwidSocket.REFRESH_TOKEN(this.prevRefreshToken));
            }

            if (this.socket.connected) {
                this.socket.disconnect();
            }
        }

        this.socket = null;
    }

    public enableDisconnect(): void {
        this.disabledDisconnect.set(false);
    }

    public disableDisconnect(): void {
        this.disabledDisconnect.set(true);
    }

    private setupSocketListeners() {
        this.socket.on(EEventKiwidSocket.CONNECT, () => {
            this.clientId.set(this.socket.id);
            this.isConnected$.next(true);
            this.reconnectAttempts = 0;
            this.retryReconnectionRefreshToken = 0;
            this.connectionEvent.emit();
            this.isReconnecting.set(false);
            this.isLoadingConnection.set(false);
        });

        this.socket.on(EEventKiwidSocket.DISCONNECT, () => {
            this.isConnected$.next(false);
            this.clientId.set(null);
            this.attemptReconnect();
            this.isLoadingConnection.set(false);
        });

        this.socket.on(EEventKiwidSocket.CONNECT_ERROR, (error) => {
            if (!!error && String(error).search(environment.socketUnauthorized) > -1) {
                refreshToken();
                return;
            }

            this.socket.disconnect();
            this.isConnected$.next(false);
            this.clientId.set(null);
            this.attemptReconnect();
            this.isLoadingConnection.set(false);
        });

        this.socket.on(EEventKiwidSocket.ERROR, (error) => {
            console.error('ERROR', { error });
        });

        let timeout: NodeJS.Timeout = null;

        const refreshToken = () => {
            if (!!timeout) {
                clearTimeout(timeout);
            }

            const disconnect = () => {
                this.isReconnecting.set(false);
                this.isLoadingConnection.set(false);
                this.disconnect();
            };

            timeout = setTimeout(() => {
                if (this.retryReconnectionRefreshToken < this.maxRetryReconnectionRefreshToken) {
                    this.retryReconnectionRefreshToken += 1;

                    this.authenticationService
                        .getRefreshToken({
                            refreshToken: this.authenticationService.refreshToken,
                        })
                        .pipe(first())
                        .subscribe({
                            next: (result) => {
                                this.authenticationService.userCookie(result);
                                onRefreshToken(result.refreshToken);
                                this.isLoadingConnection.set(false);
                                this.destroySocket();
                                this.initSocket();
                            },
                            error: () => {
                                disconnect();
                                this.authenticationService.logout(!(window.location.pathname.search('public') > -1)).then(() => {});
                            },
                        });
                } else {
                    disconnect();
                }
            }, 300);
        };

        const onRefreshToken = (refreshToken: string) => {
            if (!!this.prevRefreshToken) {
                this.socket.off(EEventKiwidSocket.REFRESH_TOKEN(this.prevRefreshToken));
            }

            this.prevRefreshToken = refreshToken;

            this.socket.on(EEventKiwidSocket.REFRESH_TOKEN(refreshToken), (data: IAuthentication) => {
                this.authenticationService.userCookie(data);
                onRefreshToken(data.refreshToken);
            });
        };

        onRefreshToken(this.authenticationService.refreshToken);

        this.socket.on(EEventKiwidSocket.EXCEPTION, (error: IErrorWebsocket) => {
            if (error.message.search(environment.socketUnauthorized) > -1) {
                refreshToken();
                return;
            }
            this.onException.emit(error);
        });
    }

    public getConnectionStatus(): Observable<boolean> {
        return this.isConnected$.asObservable();
    }

    public on(event: string, callback: (...args: any[]) => void): void {
        if (this.socket) {
            this.socket.on(event, callback);
        } else {
            console.error('Socket is not connected');
        }
    }

    public once(event: string, callback: (...args: any[]) => void): void {
        if (this.socket) {
            this.socket.once(event, callback);
        } else {
            console.error('Socket is not connected');
        }
    }

    public emit(event: string, ...args: any[]): void {
        if (this.socket) {
            this.socket.emit(event, ...args);
        } else {
            console.error('Socket is not connected');
        }
    }

    public emitWithResponse<T>(event: string, ...args: any[]): Observable<T> {
        return new Observable((observer) => {
            if (!this.socket) {
                observer.error(new Error('Socket is not connected'));
                return;
            }

            const callback = (response: IResponseSocket<T>) => {
                if (response.success) {
                    observer.next(response.data);
                    observer.complete();
                } else {
                    observer.error(response.error);
                }
            };

            const actualArgs = typeof args[args.length - 1] === 'function' ? args.slice(0, -1) : args;

            this.socket.emit(event, ...actualArgs, callback);

            return () => {};
        });
    }

    public fromEvent<T>(event: string): Observable<T> {
        return new Observable((observer) => {
            const socket = this.socket;

            if (!!socket) {
                socket.on(event, (data: T) => {
                    observer.next(data);
                });
            }

            return () => {
                if (socket?.connected) {
                    socket.off(event);
                }
            };
        });
    }

    private connect() {
        if (!!this.socket) {
            this.isLoadingConnection.set(true);
            this.socket.connect();
        }
    }

    private disconnect() {
        this.socket?.disconnect();
    }

    private attemptReconnect() {
        if (!navigator.onLine) {
            this.disconnect();
            this.isReconnecting.set(false);
            this.isConnected$.next(false);
            this.clientId.set(null);
            return;
        }

        this.isReconnecting.set(true);

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            setTimeout(() => {
                console.info(`Tentativa de reconexão ${this.reconnectAttempts + 1}`);
                this.reconnectAttempts++;
                this.connect();
            }, this.reconnectInterval);
        } else {
            this.isReconnecting.set(false);
            this.isConnected$.next(false);
            this.clientId.set(null);
        }
    }

    private handleTabHidden() {
        if (this.visibilityTimeout) {
            clearTimeout(this.visibilityTimeout);
        }

        this.visibilityTimeout = window.setTimeout(() => {
            this.ngZone.run(() => {
                this.reconnectAttempts = Number(this.maxReconnectAttempts);
                this.disconnect();
            });
        }, this.disableConnectionInterval);
    }

    private handleTabVisible() {
        if (this.visibilityTimeout) {
            clearTimeout(this.visibilityTimeout);
            this.visibilityTimeout = null;
        }

        if (!this.socket?.connected) {
            this.ngZone.run(() => {
                this.isReconnecting.set(true);
                this.connect();
            });
        }
    }
}
